import * as React from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import {
  $salesOrderLineItems,
  $syncSalesOrderLineItemsAction,
} from '~/modules/trade/_state/loadForm.state';
import { LoadFormLineItem, LoadFormState } from '~/modules/trade/types/load';

type Props = {
  loadSalesOrderId?: string | null;
  onSyncComplete?: (
    lineItems: LoadFormLineItem[],
    currentLoadForm: LoadFormState,
  ) => void;
};

export const useSyncSalesOrderLineItems = ({
  loadSalesOrderId,
  onSyncComplete,
}: Props) => {
  const syncSalesOrderLineItems = useSetAtom($syncSalesOrderLineItemsAction);
  const salesOrderLineItemsQuery = useAtomValue($salesOrderLineItems);

  // Memoize the sales order line items to avoid unnecessary re-renders
  const salesOrderLineItems = React.useMemo(
    () =>
      salesOrderLineItemsQuery.data?.salesOrders.entries[0]?.lineItems ?? [],
    [salesOrderLineItemsQuery.data],
  );

  const queriedSalesOrderId = React.useMemo(
    () => salesOrderLineItemsQuery.data?.salesOrders.entries[0]?.id,
    [salesOrderLineItemsQuery.data],
  );

  const isFetchingSalesOrderLineItems = salesOrderLineItemsQuery.isLoading;

  React.useEffect(() => {
    syncSalesOrderLineItems({
      loadSalesOrderId,
      onSyncComplete,
    });
  }, [
    loadSalesOrderId,
    onSyncComplete,
    syncSalesOrderLineItems,
    salesOrderLineItems,
    isFetchingSalesOrderLineItems,
  ]);

  return {
    isSyncing: isFetchingSalesOrderLineItems,
    salesOrderLineItems,
    queriedSalesOrderId,
  };
};
